/**
 * React Query hook for fetching timeline data
 */

import { useQuery } from '@tanstack/react-query'
import httpClient from 'src/utilities/http-client'
import { TimelineResponse } from '../types'

/**
 * Query key factory for timeline data
 * Only includes API-level filters (linkedServiceID, dates)
 */
export let timelineQueryKeys = {
  all: ['v4-dashboard', 'timeline'] as const,
  activities: (linkedServiceID: string, startDate: string, endDate: string) =>
    [
      ...timelineQueryKeys.all,
      'activities',
      linkedServiceID,
      startDate,
      endDate,
    ] as const,
}

/**
 * Fetch timeline data from API (only API-level filters)
 */
async function fetchTimelineData(
  linkedServiceID: string,
  startDate: string,
  endDate: string,
): Promise<TimelineResponse> {
  // Only send essential filters to API
  let searchParams = new URLSearchParams({
    linkedServiceID,
    startDate,
    endDate,
  })

  let response = await httpClient.get(
    `/api/manage/dashboard/timeline-activities?${searchParams}`,
  )
  return response.data
}

/**
 * React Query hook for timeline data (API-level filters only)
 */
export function useTimelineData(
  linkedServiceID: string,
  startDate: string,
  endDate: string,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: timelineQueryKeys.activities(linkedServiceID, startDate, endDate),
    queryFn: () => fetchTimelineData(linkedServiceID, startDate, endDate),
    enabled: enabled && Boolean(linkedServiceID && startDate && endDate),
  })
}
